import { DialogAdminTeamCreation } from "@/components/admin/dialog-admin-team-creation";
import DataTableAdminTeams from "@/components/data-table-admin-teams/data-table-admin-teams";
import { Button } from "@/components/ui/button";
import { FilterBar } from "@/components/ui/filter-bar";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useToastError } from "@/components/ui/use-toast";
import { useTeams } from "@/contexts/teams-provider";
import { useAdminTeamsRefreshListener } from "@/hooks/useAdminTeamsEvents";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { PlusCircledIcon, ReloadIcon } from "@radix-ui/react-icons";
import { AdminTeamDTO, AdminTeamsFilterCountsDTO } from "@shaarp/common";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryParam } from "use-query-params";

export default function AdministrationTeamsPage() {
  const api = useApi();
  const { setTeamIdSelected } = useTeams();
  const { t } = useTranslation();
  const toastError = useToastError();

  // Query params using useQueryParam
  const [filter, setFilter] = useQueryParam<string | undefined>("filter");
  const [query, setQuery] = useQueryParam<string | undefined>("query");

  const [isLoadingCounts, setIsLoadingCounts] = useState(true);
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  const [adminTeamsDto, setAdminTeamsDto] = useState<AdminTeamDTO[]>();
  const [filterCounts, setFilterCounts] = useState<AdminTeamsFilterCountsDTO>();
  const [showNewTeamDialog, setShowNewTeamDialog] = useState(false);
  const [searchInput, setSearchInput] = useState<string>("");

  const abortControllerRef = useRef<AbortController | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync search input with query param
  useEffect(() => {
    setSearchInput(query || "");
  }, [query]);

  const filterOptions = [
    {
      id: "all",
      label: t("admin.teams.filters.all"),
      number: filterCounts?.countTotal,
      tooltip: t("admin.teams.filters.tooltips.all"),
    },
    {
      id: "subscribed",
      label: t("admin.teams.filters.subscribed"),
      number: filterCounts?.countTotalTeamsSubscribers,
      subNumber: filterCounts?.countTotalLicences,
      tooltip: t("admin.teams.filters.tooltips.subscribed"),
    },
    {
      id: "subscribed-monthly",
      label: t("admin.teams.filters.subscribed-monthly"),
      number: filterCounts?.countTotalTeamsSubscribeMonthly,
      subNumber: filterCounts?.countTotalMonthlyLicences,
      tooltip: t("admin.teams.filters.tooltips.subscribedMonthly"),
    },
    {
      id: "subscribed-annual",
      label: t("admin.teams.filters.subscribed-annual"),
      number: filterCounts?.countTotalTeamsSubscribeAnnual,
      subNumber: filterCounts?.countTotalAnnualLicences,
      tooltip: t("admin.teams.filters.tooltips.subscribedAnnual"),
    },
    {
      id: "subscribed-free",
      label: t("admin.teams.filters.subscribed-free"),
      number: filterCounts?.countTeamsSubscribersFree,
      tooltip: t("admin.teams.filters.tooltips.subscribedFree"),
    },
    {
      id: "unsubscribed",
      label: t("admin.teams.filters.unsubscribed"),
      number: filterCounts?.countUnsubscribed,
      tooltip: t("admin.teams.filters.tooltips.unsubscribed"),
    },
    {
      id: "payment-failed",
      label: t("admin.teams.filters.payment-failed"),
      hasError: (filterCounts?.countPaymentFailed || 0) > 0,
      number: filterCounts?.countPaymentFailed,
      tooltip: t("admin.teams.filters.tooltips.paymentFailed"),
    },
    {
      id: "billing-error",
      label: t("admin.teams.filters.billing-error"),
      hasError: (filterCounts?.countBillingError || 0) > 0,
      number: filterCounts?.countBillingError,
      tooltip: t("admin.teams.filters.tooltips.billingError"),
    },
    {
      id: "payment-method-missing",
      label: t("admin.teams.filters.payment-method-missing"),
      hasError: (filterCounts?.countPaymentMethodMissing || 0) > 0,
      number: filterCounts?.countPaymentMethodMissing,
      tooltip: t("admin.teams.filters.tooltips.paymentMethodMissing"),
    },
    {
      id: "missing-config",
      label: t("admin.teams.filters.missing-config"),
      number: filterCounts?.countMissingConfig,
      hasWarning: (filterCounts?.countMissingConfig || 0) > 0,
      tooltip: t("admin.teams.filters.tooltips.missingConfig"),
    },
    {
      id: "subscription-ending",
      label: t("admin.teams.filters.subscription-ending"),
      number: filterCounts?.countTeamsSubscriptionsEnding,
      hasWarning: (filterCounts?.countTeamsSubscriptionsEnding || 0) > 0,
      tooltip: t("admin.teams.filters.tooltips.subscriptionEnding"),
    },
    {
      id: "trial-active",
      label: t("admin.teams.filters.trial-active"),
      number: filterCounts?.countTrialCurrent,
      tooltip: t("admin.teams.filters.tooltips.trialActive"),
    },
    {
      id: "trial-passed",
      label: t("admin.teams.filters.trial-passed"),
      number: filterCounts?.countTrialPassed,
      tooltip: t("admin.teams.filters.tooltips.trialPassed"),
    },
    {
      id: "setup",
      label: t("admin.teams.filters.setup"),
      number: filterCounts?.countSetupWaiting,
      tooltip: t("admin.teams.filters.tooltips.setup"),
    },
    {
      id: "disabled",
      label: t("admin.teams.filters.disabled"),
      number: filterCounts?.countDisabled,
      tooltip: t("admin.teams.filters.tooltips.disabled"),
    },
    {
      id: "archived",
      label: t("admin.teams.filters.archived"),
      number: filterCounts?.countArchived,
      tooltip: t("admin.teams.filters.tooltips.archived"),
    },
  ];

  const handleFilterChange = useCallback(
    (filterId: string) => {
      // Clear search when filter is selected
      setQuery(undefined);
      setFilter(filterId);
    },
    [setFilter, setQuery]
  );

  const handleSearchChange = useCallback(
    (searchValue: string) => {
      setSearchInput(searchValue);

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce the search
      searchTimeoutRef.current = setTimeout(() => {
        if (searchValue.trim()) {
          // Clear filter when search is active
          setFilter(undefined);
          setQuery(searchValue.trim());
        } else {
          // Remove query param when search is empty
          setQuery(undefined);
        }
      }, 800); // 800ms debounce
    },
    [setFilter, setQuery]
  );

  const fetchCounts = useCallback(async () => {
    setIsLoadingCounts(true);
    try {
      const { data } = await api.get<AdminTeamsFilterCountsDTO>(
        `/admin/teams/filter-counts`
      );
      setFilterCounts(data);
    } catch (error) {
      toastError();
      setFilterCounts(undefined);
    } finally {
      setIsLoadingCounts(false);
    }
  }, []);

  const fetchTeams = useCallback(
    async (currentFilter?: string, currentQuery?: string) => {
      // Ne pas récupérer les équipes si aucun filtre n'est sélectionné et aucune recherche active
      if (!currentFilter && !currentQuery?.trim()) {
        setAdminTeamsDto([]);
        setIsLoadingTeams(false);
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      setIsLoadingTeams(true);
      try {
        const params: { filter?: string; query?: string } = {};

        // Query takes priority over filter
        if (currentQuery?.trim()) {
          params.query = currentQuery.trim();
        } else if (currentFilter && currentFilter !== "all") {
          params.filter = currentFilter;
        }

        const { data } = await api.get<AdminTeamDTO[]>(`/admin/teams`, {
          params,
          signal,
        });

        if (!signal.aborted) {
          setAdminTeamsDto(data);
        }
      } catch (error: any) {
        if (error?.name !== "AbortError" && error?.code !== "ERR_CANCELED") {
          toastError();
          setAdminTeamsDto(undefined);
        }
      } finally {
        if (!signal.aborted) {
          setIsLoadingTeams(false);
        }
      }
    },
    []
  );

  // Fetch data when filter or query changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTeams(filter, query);
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [filter, query, fetchTeams]);

  // Initial data fetch
  useEffect(() => {
    fetchCounts();
  }, []);

  // Listen for admin teams refresh events (triggered by dialogs/cells)
  useAdminTeamsRefreshListener(() => {
    fetchTeams(filter, query);
    fetchCounts();
  });

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const onNewTeamCreated = (newTeam: AdminTeamDTO) => {
    setShowNewTeamDialog(false);
    setTeamIdSelected(newTeam.id);
    fetchCounts();
    fetchTeams(filter, query);
  };

  const hasActiveFilterOrSearch = filter || query?.trim();

  return (
    <>
      <div className="overflow-auto absolute top-0 left-0 flex-1 p-8 pt-6 compact:p-6 compact:pt-4 space-y-4 w-full h-full">
        <div className="flex-1 p-8 pt-0 space-y-5">
          <div className="flex justify-between">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold tracking-tight">
                {t("admin.teams.title")}
              </h2>
              <p className="text-muted-foreground">
                {t("admin.teams.subtitle")}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Input
                type="text"
                placeholder={t("admin.teams.search.placeholder")}
                value={searchInput}
                onChange={(e) => {
                  handleSearchChange(e.target.value);
                }}
                className="w-64 bg-background"
              />

              <Button
                variant="outline"
                onClick={() => setShowNewTeamDialog(true)}
              >
                <PlusCircledIcon className="mr-2 w-5 h-5" />
                {t("admin.teams.actions.newTeam")}
              </Button>

              <Button
                variant="outline"
                onClick={() => {
                  fetchTeams(filter, query);
                }}
                disabled={!hasActiveFilterOrSearch || isLoadingTeams}
              >
                <ReloadIcon className="mr-2 w-4 h-4" />
                {t("admin.teams.actions.refresh")}
              </Button>
            </div>
          </div>

          <Separator className="my-6" />

          <FilterBar
            options={filterOptions}
            selectedFilter={filter || null}
            onFilterChange={handleFilterChange}
            className="mb-4"
            isLoading={isLoadingCounts}
          />

          {hasActiveFilterOrSearch ? (
            <DataTableAdminTeams
              teams={adminTeamsDto || []}
              isLoading={isLoadingTeams}
            />
          ) : (
            <div className="flex justify-center items-center h-full">
              <p className="text-muted-foreground">
                {t("admin.teams.selectFilter")}
              </p>
            </div>
          )}
        </div>
      </div>

      <DialogAdminTeamCreation
        open={showNewTeamDialog}
        onCreated={onNewTeamCreated}
        onClose={() => setShowNewTeamDialog(false)}
      />
    </>
  );
}
